import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import {
  RedemptionRequestInput,
  RedemptionRequestStatus,
} from 'shared/src/api/types'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { addApi } from 'shared/src/api'
import { formatPriceUsd } from 'shared/src/utils/formatting'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { FormProps } from 'components/resource'
import { NumberField, TextArea, TextField } from 'components/TextField'
import { ResourceSelectField } from './ResourceSelectField'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'

const getAvailableBalance = (
  id: number | undefined,
  availableBalance: number | undefined,
  actualPrice: string | undefined
) => {
  if (!id || !actualPrice) return availableBalance
  if (!availableBalance) return null
  return availableBalance + parseFloat(actualPrice)
}

export interface FormValues
  extends Omit<Partial<RedemptionRequestInput>, 'inputPrice' | 'actualPrice'> {
  inputPrice?: string
  actualPrice?: string
}

export const RedemptionRequestForm: FC<FormProps<FormValues>> = flowMax(
  addDisplayName('RedemptionRequestForm'),
  addHandlers({
    onStatusChange:
      ({ values, onChange }) =>
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        onChange({
          ...values,
          status: event.target.value as RedemptionRequestStatus,
        })
      },
  }),
  addApi,
  addDataLoadingFromPromise(
    'earningsOverview',
    ({ values: { userId }, api }) =>
      userId
        ? api.creatorEarnings.creatorOverview(userId)
        : Promise.resolve(undefined),
    ['userId', 'api']
  ),
  addCmsLoadingAndErrorIndicators('earningsOverview'),
  addState(
    'availableBalance',
    'setAvailableBalance',
    ({ id, earningsOverview, values: { actualPrice } }) =>
      getAvailableBalance(id, earningsOverview?.availableBalance, actualPrice)
  ),
  addProps(({ values: { userId } }) => ({
    userId: userId || null,
  })),
  addEffect(
    ({ id, values: { actualPrice }, userId, setAvailableBalance, api }) =>
      () => {
        if (userId) {
          api.creatorEarnings
            .creatorOverview(userId)
            .then((earningsOverview) => {
              setAvailableBalance(
                getAvailableBalance(
                  id,
                  earningsOverview?.availableBalance,
                  actualPrice
                )
              )
            })
        } else {
          setAvailableBalance(null)
        }
      },
    ['userId']
  ),
  addTranslationHelpers,
  ({ id, values, availableBalance, onStatusChange, onChange, errors, t }) => (
    <div>
      <p>{t('admin.resources.redemptionRequests.availableBalance')}</p>
      <p>{availableBalance ? formatPriceUsd(availableBalance) : '--'}</p>
      <ResourceSelectField
        resourceName="users"
        selectedId={values.userId}
        onChange={(userId) => onChange({ ...values, userId })}
        disabled={!!id}
        disableCreate
      />
      <TextField
        id="url"
        label={t('admin.resources.redemptionRequests.fields.url')}
        value={values.url}
        onChange={(url) => onChange({ ...values, url })}
        error={errors.url}
      />
      <NumberField
        id="inputPrice"
        label={t('admin.resources.redemptionRequests.fields.inputPrice')}
        value={values.inputPrice?.toString()}
        onChange={(inputPrice) => onChange({ ...values, inputPrice })}
        error={errors.inputPrice}
      />
      <NumberField
        id="actualPrice"
        label={t('admin.resources.redemptionRequests.fields.actualPrice')}
        value={values.actualPrice?.toString()}
        onChange={(actualPrice) => onChange({ ...values, actualPrice })}
      />
      <TextField
        id="size"
        label={t('admin.resources.redemptionRequests.fields.size')}
        value={values.size}
        onChange={(size) => onChange({ ...values, size })}
      />
      <TextField
        id="variant"
        label={t('admin.resources.redemptionRequests.fields.variant')}
        value={values.variant}
        onChange={(variant) => onChange({ ...values, variant })}
      />
      <TextField
        id="email"
        label={t('admin.resources.redemptionRequests.fields.email')}
        value={values.email}
        onChange={(email) => onChange({ ...values, email })}
        error={errors.email}
      />
      <TextField
        id="shippingAddress"
        label={t('admin.resources.redemptionRequests.fields.shippingAddress')}
        value={values.shippingAddress}
        onChange={(shippingAddress) => onChange({ ...values, shippingAddress })}
        error={errors.shippingAddress}
      />
      <TextArea
        id="notes"
        label={t('admin.resources.redemptionRequests.fields.notes')}
        value={values.notes}
        onChange={(notes) => onChange({ ...values, notes })}
      />
      <select
        value={values.status}
        onChange={onStatusChange}
        disabled={values.status === 'deleted'}
      >
        <option value="unprocessed">
          {t(
            'admin.resources.redemptionRequests.fields.statusOptions.unprocessed'
          )}
        </option>
        <option value="processing">
          {t(
            'admin.resources.redemptionRequests.fields.statusOptions.processing'
          )}
        </option>
        <option value="fulfilled">
          {t(
            'admin.resources.redemptionRequests.fields.statusOptions.fulfilled'
          )}
        </option>
        <option value="error">
          {t('admin.resources.redemptionRequests.fields.statusOptions.error')}
        </option>
        <option value="deleted">
          {t('admin.resources.redemptionRequests.fields.statusOptions.deleted')}
        </option>
      </select>
    </div>
  )
)
